/**
 * Profile Questionnaire — React harness.
 *
 * This is the ONLY place where the pure Elm-ish reducer is bridged
 * to React. It runs `update()` synchronously inside `useReducer`,
 * queues effects, and executes them outside the reducer via
 * `useEffect`.
 *
 * Elm-ish doctrine:
 *   - No `useState` in components for domain state.
 *   - No API calls from components.
 *   - No second state authority. One source of truth: the reducer.
 */

import { useCallback, useEffect, useReducer, useRef } from 'react';
import {
  initialCreateModel,
  initialEditModel,
  type ProfileFormModel,
  type PhotoEntry,
} from './model';
import type { ProfileFormMode } from './model';
import type { ProfileQuestionnaireMsg } from './messages';
import { update, type ProfileQuestionnaireEffect } from './update';
import { runEffect } from './effects';
import type { RegistrationKind } from '@/store/authStore';
import { defaultObjectUrlPort } from '@/api/objectUrlPort';

export interface UseProfileQuestionnaireProgramArgs {
  readonly mode: ProfileFormMode;
  readonly registrationKind: RegistrationKind;
  /**
   * B-2.B: object-URL port for preview lifecycle. Defaults to the
   * real browser implementation. Tests may inject a recording port.
   */
  readonly objectUrlPort?: import('@/api/objectUrlPort').ObjectUrlPort;
}

export interface UseProfileQuestionnaireProgramResult {
  readonly model: ProfileFormModel;
  readonly start: () => void;
  readonly nameChanged: (value: string) => void;
  readonly genderChanged: (value: 'male' | 'female' | undefined) => void;
  readonly scenarioChanged: (value: 'equal' | 'sponsor' | 'no_work' | undefined) => void;
  readonly ageChanged: (value: number | undefined) => void;
  readonly cityChanged: (value: string) => void;
  readonly headlineChanged: (value: string) => void;
  readonly bioChanged: (value: string) => void;
  readonly workSphereChanged: (value: string | undefined) => void;
  readonly educationSphereChanged: (value: string | undefined) => void;
  readonly educationTypeChanged: (value: string | undefined) => void;
  // B-2.B photo lifecycle:
  /**
   * User selected one or more local files. The view is responsible
   * for allocating the object-URL preview (via the injected
   * `ObjectUrlPort`) before dispatching; the reducer appends the
   * entries to `transientPhotos` and dispatches `UploadPhoto` for
   * each one.
   */
  readonly photoFilesSelected: (entries: readonly PhotoEntry[]) => void;
  /**
   * Begin uploading a specific pending entry (typically dispatched
   * immediately after `photoFilesSelected`, but exposed for retry
   * from the failed-upload affordance).
   */
  readonly photoUploadRequested: (localId: string) => void;
  readonly photoDeleteRequested: (id: string) => void;
  /**
   * Drop a pending (non-durable) entry and release its object-URL
   * preview. Idempotent.
   */
  readonly photoPreviewReleased: (localId: string) => void;
  readonly submit: () => void;
  readonly retry: () => void;
  readonly dismissCompletionModal: () => void;
  readonly s005StartRequested: () => void;
  // B-2.C skills callbacks
  readonly skillsTaxonomyLoadRequested: () => void;
  readonly skillsProfileLoadRequested: () => void;
  readonly skillSelected: (skillId: string) => void;
  readonly skillRemoved: (skillId: string) => void;
  readonly skillsSearchChanged: (value: string) => void;
  readonly skillsCategoryChanged: (value: string | null) => void;
  readonly skillsSaveRequested: () => void;
}

/**
 * CORRECTION10 — explicit command mailbox.
 *
 * `pendingEffects` is now a monotonic list of `{ id, effect }`
 * records. IDs are assigned INSIDE the pure reducer wrapper
 * (programReducer) starting at `nextEffectId`. The effect
 * runner claims an ID synchronously before invoking the
 * interpreter and dispatches `EffectsAcknowledged(ids)` so
 * the wrapper can remove the claimed command from the
 * mailbox — without losing commands queued by earlier
 * dispatches in the same React-18 batched event.
 *
 * Why this matters:
 *
 * Previously, `pendingEffects` was a single replaceable
 * value produced by each `update()` call. When the user
 * selected 3 photos in one event, the reducer ran 3 times.
 * The first call produced `effects: [UploadPhoto(A)]`, but
 * subsequent calls (with `alreadyUploading=true`) returned
 * `effects: []`. The wrapper then replaced `pendingEffects`
 * with `[]`, wiping the queued `UploadPhoto(A)`. The effect
 * runner saw an empty mailbox and never reached the network
 * adapter. The cascade hung.
 *
 * With the mailbox, every dispatched message's newly-emitted
 * commands are APPENDED to the prior queue with fresh IDs,
 * not replacing it. The drain happens via an explicit
 * `EffectsAcknowledged` action that removes by ID.
 */
interface PendingEffect {
  readonly id: number;
  readonly effect: ProfileQuestionnaireEffect;
}

interface ProgramState {
  readonly model: ProfileFormModel;
  readonly pendingEffects: readonly PendingEffect[];
  readonly nextEffectId: number;
}

function initProgramState(mode: ProfileFormMode): ProgramState {
  const model =
    mode.kind === 'edit' ? initialEditModel(mode.profileId) : initialCreateModel;
  return { model, pendingEffects: [], nextEffectId: 1 };
}

/**
 * Pure reducer wrapper (Elm-ish: programState × Msg → programState).
 *
 * For domain messages, runs `update()` on the model and APPENDS
 * the resulting effects to the existing mailbox with fresh IDs.
 * For `EffectsAcknowledged`, removes the claimed IDs from the
 * mailbox. Never overwrites prior queued effects.
 *
 * This wrapper is a pure function: given the same input
 * `(state, msg)` it always returns the same output. React
 * may invoke it twice in development under StrictMode; both
 * calls return the same output, and only the second result
 * is committed. That guarantees no double-claim of IDs.
 */
function programReducer(
  currentState: ProgramState,
  msg: ProfileQuestionnaireMsg
): ProgramState {
  if (msg.type === 'EffectsAcknowledged') {
    const ids = new Set<number>(msg.ids);
    const remaining = currentState.pendingEffects.filter(
      (p) => !ids.has(p.id)
    );
    if (remaining.length === currentState.pendingEffects.length) {
      // Nothing to acknowledge — return same reference so React
      // does not re-render unnecessarily.
      return currentState;
    }
    return { ...currentState, pendingEffects: remaining };
  }
  const result = update(currentState.model, msg);
  const newEffects: readonly PendingEffect[] = result.effects.map(
    (effect, i) => ({
      id: currentState.nextEffectId + i,
      effect,
    })
  );
  if (newEffects.length === 0 && result.model === currentState.model) {
    // Pure reducer produced no observable change — keep references
    // stable so React skips the re-render.
    return currentState;
  }
  return {
    model: result.model,
    pendingEffects: [
      ...currentState.pendingEffects,
      ...newEffects,
    ],
    nextEffectId: currentState.nextEffectId + newEffects.length,
  };
}

export function useProfileQuestionnaireProgram(
  args: UseProfileQuestionnaireProgramArgs
): UseProfileQuestionnaireProgramResult {
  const { mode, registrationKind, objectUrlPort } = args;
  const dispatchRef = useRef<(msg: ProfileQuestionnaireMsg) => void>(() => {});

  const [state, baseDispatch] = useReducer(
    programReducer,
    mode,
    initProgramState
  );

  dispatchRef.current = baseDispatch;

  // CORRECTION10 — claim-and-drain effect runner.
  //
  // The instance-local `claimedEffectIdsRef` is the authoritative
  // "we have already kicked off this command" set. It survives
  // StrictMode setup/cleanup/setup cycles because it is a useRef.
  //
  // Each time `state.pendingEffects` changes:
  //   1. Filter to entries whose ID is not yet claimed.
  //   2. Mark them as claimed synchronously.
  //   3. Dispatch `EffectsAcknowledged(ids)` so the wrapper removes
  //      them from the mailbox.
  //   4. Invoke `runEffect` for each claimed command.
  //
  // This sequence guarantees:
  //   - StrictMode's setup- → cleanup- → setup- sequence only
  //     executes each command ONCE (second setup finds all
  //     already-claimed IDs and returns early).
  //   - The mailbox is drained after the claim, so subsequent
  //     useEffect fires do not re-run the same command.
  //   - Commands queued by a domain dispatch in the same React
  //     batch as the drain are NOT lost — the drain is a
  //     separate dispatch and appends happen after the drain.
  const claimedEffectIdsRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    const claimed = claimedEffectIdsRef.current;
    const toRun = state.pendingEffects.filter((p) => !claimed.has(p.id));
    if (toRun.length === 0) return;
    // 1) Mark as claimed synchronously, before any dispatch or
    //    network call. StrictMode's re-mount sees these IDs as
    //    already claimed and skips them.
    for (const p of toRun) claimed.add(p.id);
    // 2) Dispatch the drain. This is a separate React state update
    //    that removes the claimed IDs from the mailbox.
    baseDispatch({
      type: 'EffectsAcknowledged',
      ids: toRun.map((p) => p.id),
    });
    // 3) Invoke the interpreter for each claimed command. Any
    //    follow-up dispatch from these effects (e.g.
    //    PhotoUploadSucceeded) goes through the wrapper and is
    //    appended to the mailbox under a fresh ID.
    const cleanupFunctions: (() => void)[] = [];
    for (const p of toRun) {
      const cleanup = runEffect(p.effect, {
        dispatch: baseDispatch,
        registrationKind,
        ...(objectUrlPort !== undefined && { objectUrlPort }),
      });
      if (cleanup && typeof (cleanup as { then?: unknown }).then === 'function') {
        (cleanup as Promise<(() => void) | void>).then((fn) => {
          if (typeof fn === 'function') cleanupFunctions.push(fn);
        });
      }
    }
    return () => {
      for (const fn of cleanupFunctions) fn();
    };
  }, [state.pendingEffects, baseDispatch, registrationKind, objectUrlPort]);

  // CORRECTION01 R3 — Object-URL teardown on unmount.
  //
  // When the React component that owns this program unmounts (page
  // navigation, hard reload, etc.), any pending local photo entries
  // with `blob:` URLs MUST have those URLs revoked. Otherwise the
  // blob remains referenced until garbage collected (typically
  // process exit) and leaks memory.
  //
  // We track the URLs we have seen via a ref so the cleanup can
  // iterate over what is currently outstanding. Each entry also
  // emits RevokeObjectURLPreview effects during normal lifecycle
  // (success / cancel / delete / failed removal); the unmount
  // cleanup is the last-resort safety net for any URL that escaped
  // the normal flow (e.g. mid-upload when the component unmounts).
  const port = objectUrlPort ?? defaultObjectUrlPort;
  const seenBlobUrlsRef = useRef<Set<string>>(new Set());
  // Track every transient blob URL that currently exists.
  useEffect(() => {
    const current = new Set<string>();
    for (const entry of state.model.transientPhotos) {
      if (entry.url && entry.url.startsWith('blob:')) {
        current.add(entry.url);
      }
    }
    seenBlobUrlsRef.current = current;
  }, [state.model.transientPhotos]);
  // Unmount cleanup: revoke every outstanding blob URL exactly once.
  useEffect(() => {
    return () => {
      for (const url of seenBlobUrlsRef.current) {
        port.revokeObjectURL(url);
      }
      seenBlobUrlsRef.current.clear();
    };
  }, [port]);

  const start = useCallback(() => baseDispatch({ type: 'Started' }), []);
  const nameChanged = useCallback(
    (value: string) => baseDispatch({ type: 'NameChanged', value }),
    []
  );
  const genderChanged = useCallback(
    (value: 'male' | 'female' | undefined) =>
      baseDispatch({ type: 'GenderChanged', value }),
    []
  );
  const scenarioChanged = useCallback(
    (value: 'equal' | 'sponsor' | 'no_work' | undefined) =>
      baseDispatch({ type: 'ScenarioChanged', value }),
    []
  );
  const ageChanged = useCallback(
    (value: number | undefined) => baseDispatch({ type: 'AgeChanged', value }),
    []
  );
  const cityChanged = useCallback(
    (value: string) => baseDispatch({ type: 'CityChanged', value }),
    []
  );
  const headlineChanged = useCallback(
    (value: string) => baseDispatch({ type: 'HeadlineChanged', value }),
    []
  );
  const bioChanged = useCallback(
    (value: string) => baseDispatch({ type: 'BioChanged', value }),
    []
  );
  const workSphereChanged = useCallback(
    (value: string | undefined) =>
      baseDispatch({ type: 'WorkSphereChanged', value }),
    []
  );
  const educationSphereChanged = useCallback(
    (value: string | undefined) =>
      baseDispatch({ type: 'EducationSphereChanged', value }),
    []
  );
  const educationTypeChanged = useCallback(
    (value: string | undefined) =>
      baseDispatch({ type: 'EducationTypeChanged', value }),
    []
  );
  const photoFilesSelected = useCallback(
    (entries: readonly PhotoEntry[]) => {
      for (const entry of entries) {
        baseDispatch({ type: 'PhotoSelected', entry });
      }
    },
    []
  );
  const photoUploadRequested = useCallback(
    (localId: string) => {
      // CORRECTION01 R2: retry-from-failed-upload.
      // The reducer looks up the targeted transient entry, requires
      // its File, flips kind='uploading', AND emits a real
      // UploadPhoto effect so the harness re-runs the role-aware
      // adapter. The previous behavior only flipped the kind but
      // emitted no effect, leaving the user in a spinner-forever
      // state.
      baseDispatch({ type: 'PhotoUploadStarted', id: localId });
    },
    []
  );
  const photoDeleteRequested = useCallback(
    (id: string) => baseDispatch({ type: 'PhotoDeleteRequested', id }),
    []
  );
  const photoPreviewReleased = useCallback(
    (localId: string) =>
      baseDispatch({ type: 'PhotoPreviewReleased', id: localId }),
    []
  );
  const submit = useCallback(
    () => baseDispatch({ type: 'SubmitRequested' }),
    []
  );
  const retry = useCallback(
    () => baseDispatch({ type: 'RetryRequested' }),
    []
  );
  const dismissCompletionModal = useCallback(
    () => baseDispatch({ type: 'CompletionModalDismissed' }),
    []
  );
  const s005StartRequested = useCallback(
    () => baseDispatch({ type: 'S005StartRequested' }),
    []
  );

  // B-2.C skills callbacks. Each dispatch creates a fresh message
  // identity; the reducer is pure and the mailbox handles dedup.
  const skillsTaxonomyLoadRequested = useCallback(
    () => baseDispatch({ type: 'SkillsTaxonomyLoadRequested' }),
    []
  );
  const skillsProfileLoadRequested = useCallback(
    () => baseDispatch({ type: 'ProfileSkillsLoadRequested' }),
    []
  );
  const skillSelected = useCallback(
    (skillId: string) => baseDispatch({ type: 'SkillSelected', skillId }),
    []
  );
  const skillRemoved = useCallback(
    (skillId: string) => baseDispatch({ type: 'SkillRemoved', skillId }),
    []
  );
  const skillsSearchChanged = useCallback(
    (value: string) =>
      baseDispatch({ type: 'SkillsSearchChanged', value }),
    []
  );
  const skillsCategoryChanged = useCallback(
    (value: string | null) =>
      baseDispatch({ type: 'SkillsCategoryChanged', value }),
    []
  );
  const skillsSaveRequested = useCallback(
    () => baseDispatch({ type: 'ProfileSkillsSaveRequested' }),
    []
  );

  return {
    model: state.model,
    start,
    nameChanged,
    genderChanged,
    scenarioChanged,
    ageChanged,
    cityChanged,
    headlineChanged,
    bioChanged,
    workSphereChanged,
    educationSphereChanged,
    educationTypeChanged,
    photoFilesSelected,
    photoUploadRequested,
    photoDeleteRequested,
    photoPreviewReleased,
    submit,
    retry,
    dismissCompletionModal,
    s005StartRequested,
    skillsTaxonomyLoadRequested,
    skillsProfileLoadRequested,
    skillSelected,
    skillRemoved,
    skillsSearchChanged,
    skillsCategoryChanged,
    skillsSaveRequested,
  };
}